import {
	Document,
	Page,
	PDFViewer,
	StyleSheet,
	Text,
	View,
} from "@react-pdf/renderer";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { CommonData, Detail, getDetails, Person, SessionState } from "../utils";
import { DetailsType } from "./DetailsGrid";

const styles = StyleSheet.create({
	page: {
		backgroundColor: "#fff",
		fontSize: "10px",
		paddingTop: "45px",
		paddingBottom: "45px",
	},
	titleContainer: {
		flexDirection: "row",
	},
	title: {
		fontSize: "14px",
		padding: "2px",
		width: "50%",
	},
	date: {
		padding: "2px",
		width: "50%",
		textAlign: "right",
		verticalAlign: "sub",
	},
	table: {
		marginTop: "8px",
	},
	headerRow: {
		flexDirection: "row",
		backgroundColor: "#666",
		color: "#fff",
	},
	row: {
		flexDirection: "row",
		borderBottom: "1px dotted black",
	},
	passedRow: {
		flexDirection: "row",
		borderBottom: "1px dotted black",
		color: "#aaa",
	},
	dateRow: {
		width: "20%",
		padding: "4px",
	},
	assigneeRow: {
		width: "20%",
		padding: "4px",
	},
	descriptionRow: {
		width: "60%",
		padding: "4px",
	},
	section: {
		margin: 10,
		padding: 10,
		flexGrow: 1,
	},
	detailsHeader: {
		paddingTop: "16px",
		fontSize: "12px",
	},
});

const PDFDocument = ({
	commonData,
	details,
	people,
}: {
	commonData: CommonData;
	people: Person[];
	caseData: { [id: string]: string };
	details: { notes: Detail[]; tasks: Detail[]; deadlines: Detail[] };
}) => {
	const { caseNumber } = commonData;
	const { notes, tasks, deadlines } = details;
	return (
		<Document>
			<Page size="LETTER" style={styles.page}>
				<View style={styles.section}>
					<View style={styles.titleContainer}>
						<Text style={styles.title}>
							Details for Case Number {caseNumber}
						</Text>
					</View>
					<View style={styles.detailsHeader}>
						<Text>Notes</Text>
					</View>
					<View style={styles.table}>
						<View style={styles.headerRow}>
							<Text style={styles.dateRow}>Date</Text>
							<Text style={styles.assigneeRow}>Assignee</Text>
							<Text style={styles.descriptionRow}>
								Description
							</Text>
						</View>
						{notes.map(({ date, assignees, description }) => (
							<View style={styles.row}>
								<Text style={styles.dateRow}>
									{dayjs(date).format("MM/DD/YYYY hh:mm A")}
								</Text>
								<Text style={styles.assigneeRow}>
									{[
										...people
											.filter(({ _id }) =>
												assignees.includes(_id)
											)
											.map(({ name }) => name),
										...assignees.filter(
											(_id) =>
												people.find(
													(person) =>
														person._id === _id
												) === undefined
										),
									].join(", ")}
								</Text>
								<Text style={styles.descriptionRow}>
									{description}
								</Text>
							</View>
						))}
					</View>
					<View style={styles.detailsHeader}>
						<Text>Tasks</Text>
					</View>
					<View style={styles.table}>
						<View style={styles.headerRow}>
							<Text style={styles.dateRow}>Date</Text>
							<Text style={styles.assigneeRow}>Assignee</Text>
							<Text style={styles.descriptionRow}>
								Description
							</Text>
						</View>
						{tasks.map(
							({ date, assignees, description, isComplete }) => (
								<View style={styles.row}>
									<Text
										style={{
											padding: "4px",
											left: "-10px",
											position: "absolute",
										}}
									>
										{isComplete ? "×" : ""}
									</Text>
									<Text style={styles.dateRow}>
										{dayjs(date).format(
											"MM/DD/YYYY hh:mm A"
										)}
									</Text>
									<Text style={styles.assigneeRow}>
										{[
											...people
												.filter(({ _id }) =>
													assignees.includes(_id)
												)
												.map(({ name }) => name),
											...assignees.filter(
												(_id) =>
													people.find(
														(person) =>
															person._id === _id
													) === undefined
											),
										].join(", ")}
									</Text>
									<Text style={styles.descriptionRow}>
										{description}
									</Text>
								</View>
							)
						)}
					</View>
					<View style={styles.detailsHeader}>
						<Text>Deadlines</Text>
					</View>
					<View style={styles.table}>
						<View style={styles.headerRow}>
							<Text style={styles.dateRow}>Date</Text>
							<Text style={styles.assigneeRow}>Assignee</Text>
							<Text style={styles.descriptionRow}>
								Description
							</Text>
						</View>
						{deadlines.map(
							({ date, assignees, description, isComplete }) => {
								const isPassed =
									dayjs(date).diff(dayjs(), "days") < -1;
								return (
									<View
										style={
											isPassed
												? styles.passedRow
												: styles.row
										}
									>
										<Text
											style={{
												padding: "4px",
												left: "-10px",
												position: "absolute",
											}}
										>
											{isComplete ? "×" : ""}
										</Text>
										<Text style={styles.dateRow}>
											{dayjs(date).format(
												"MM/DD/YYYY hh:mm A"
											)}
										</Text>
										<Text style={styles.assigneeRow}>
											{[
												...people
													.filter(({ _id }) =>
														assignees.includes(_id)
													)
													.map(({ name }) => name),
												...assignees.filter(
													(_id) =>
														people.find(
															(person) =>
																person._id ===
																_id
														) === undefined
												),
											].join(", ")}
										</Text>
										<Text style={styles.descriptionRow}>
											{description}
										</Text>
									</View>
								);
							}
						)}
					</View>
				</View>
			</Page>
		</Document>
	);
};

export const DetailsPDF = ({
	people,
	commonData,
	caseData,
	id,
	sessionState,
	setSessionState,
}: {
	people: Person[];
	commonData: CommonData;
	caseData: { [id: string]: string };
	id: string;
	sessionState: SessionState;
	setSessionState: (arg0: SessionState) => void;
}) => {
	const notesQuery = useQuery({
		queryKey: ["details", DetailsType.Note, id],
		queryFn: () =>
			getDetails(
				{
					archived: false,
					caseId: id,
					type: "note",
				},
				sessionState,
				setSessionState
			),
	});
	const tasksQuery = useQuery({
		queryKey: ["details", DetailsType.Task, id],
		queryFn: () =>
			getDetails(
				{
					archived: false,
					caseId: id,
					type: "task",
				},
				sessionState,
				setSessionState
			),
	});
	const deadlinesQuery = useQuery({
		queryKey: ["details", DetailsType.Deadline, id],
		queryFn: () =>
			getDetails(
				{
					archived: false,
					caseId: id,
					type: "deadline",
				},
				sessionState,
				setSessionState
			),
	});
	return (
		<PDFViewer
			style={{
				width: "100%",
				height: "100%",
			}}
		>
			<PDFDocument
				people={people}
				commonData={commonData}
				caseData={caseData}
				details={{
					notes: (notesQuery.data || [])
						.map((detail: Detail & { assignee: string }) => ({
							...detail,
							assignee: undefined,
							assignees:
								detail.assignees === undefined
									? [detail.assignee]
									: detail.assignees,
						}))
						.toSorted(
							(a: { date: string }, b: { date: string }) =>
								-dayjs(a.date).diff(dayjs(b.date))
						),
					tasks: (tasksQuery.data || [])
						.map((detail: Detail & { assignee: string }) => ({
							...detail,
							assignee: undefined,
							assignees:
								detail.assignees === undefined
									? [detail.assignee]
									: detail.assignees,
						}))
						.toSorted((a: { date: string }, b: { date: string }) =>
							dayjs(a.date).diff(dayjs(b.date))
						),
					deadlines: (deadlinesQuery.data || [])
						.map((detail: Detail & { assignee: string }) => ({
							...detail,
							assignee: undefined,
							assignees:
								detail.assignees === undefined
									? [detail.assignee]
									: detail.assignees,
						}))
						.toSorted((a: { date: string }, b: { date: string }) =>
							dayjs(a.date).diff(dayjs(b.date))
						),
				}}
			/>
		</PDFViewer>
	);
};
